import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { OnboardingShell } from '@/onboarding/OnboardingShell'
import { useChannel } from '@/context/ChannelContext'
import { usePinnedChannelUUID } from '@/onboarding/usePinnedChannelUUID'
import { bookmarkStep } from '@/api/onboarding'
import { track } from '@/lib/analytics'

// Fallback-страница: попадает сюда юзер из ошибки INSUFFICIENT_POSTS
// (на creatives-editor нажал «Описать вручную →» — приходит state.openFallback=true).
// Если зашли сюда без openFallback (старый bookmark, прямой URL) — редиректим
// на creatives-editor: пусть сначала попробует автогенерацию по постам канала.
export function AdGenPreparePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const openFallback = (location.state as { openFallback?: boolean } | null)?.openFallback === true

  useEffect(() => {
    if (openFallback) return
    bookmarkStep('creatives-editor')
    navigate('/onboarding/creatives-editor', { replace: true })
  }, [openFallback, navigate])

  const { channels } = useChannel()
  const channelUUID = usePinnedChannelUUID()
  const channelTitle = channels.find((c) => c.uuid === channelUUID)?.title
  const [fallbackText, setFallbackText] = useState('')

  if (!openFallback) {
    return <OnboardingShell step="ad-gen-prepare" variant="minimal"><div style={{ minHeight: '60vh' }} /></OnboardingShell>
  }

  function handleNext() {
    track('creative_fallback_submitted', {
      length: fallbackText.trim().length,
      message: `ручное описание · ${fallbackText.trim().length} симв`,
    })
    bookmarkStep('creatives-editor')
    navigate('/onboarding/creatives-editor', {
      state: { fallbackText: fallbackText.trim() || undefined },
    })
  }

  const canSubmit = fallbackText.trim().length >= 50

  return (
    <OnboardingShell
      step="ad-gen-prepare"
      footer={
        <>
          <button className="foot-back" onClick={() => navigate('/onboarding/creatives-editor')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Назад
          </button>
          <div className="foot-spacer" />
          <button className="foot-next" onClick={handleNext} disabled={!canSubmit}>
            Сгенерировать креативы
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </>
      }
    >
      <p className="stage-eyebrow">
        <span className="stage-eyebrow-pill">AI‑генератор объявлений</span>
        Описание вручную
      </p>
      <h1 className="stage-title">
        Расскажите <em>про&nbsp;аудиторию</em>
      </h1>
      <p className="stage-sub">
        В канале{channelTitle ? <> «{channelTitle}»</> : ''} мало постов для&nbsp;автоматического анализа. Опишите своими словами&nbsp;— про&nbsp;что канал, кого хотите привлечь&nbsp;— и&nbsp;мы&nbsp;соберём креативы по&nbsp;описанию.
      </p>

      <div className="manual-card">
        <label className="manual-label" htmlFor="manual-desc">Описание канала и&nbsp;целевой аудитории</label>
        <textarea
          id="manual-desc"
          className="manual-textarea"
          rows={6}
          placeholder="Например: я веду канал про ремесленный кофе. Хочу привлечь кофеманов 25–40, бариста и владельцев кофеен. Делюсь обзорами зёрен, рецептами, разборами кофеен Москвы и Питера. Тон дружелюбный, без продаж в лоб."
          value={fallbackText}
          onChange={(e) => setFallbackText(e.target.value)}
          autoFocus
        />
        <div className="manual-footer">
          <span className="manual-count">{fallbackText.length} символов</span>
          <span className="manual-tip">Оптимально 200–400 · минимум 50</span>
        </div>
      </div>

      <style>{`
        .manual-card {
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 16px;
          padding: 22px 24px;
          max-width: 760px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .manual-label {
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--tg-mute, #8f9eb0);
        }
        .manual-textarea {
          width: 100%;
          padding: 14px;
          border: 1.5px solid var(--tg-line-2, #f0f0f4);
          border-radius: 10px;
          font-family: inherit;
          font-size: 14px;
          color: var(--tg-ink, #111);
          line-height: 1.55;
          resize: vertical;
          outline: none;
          transition: border-color 150ms;
          box-sizing: border-box;
        }
        .manual-textarea:focus { border-color: var(--tg-blue, #0080ff); }
        .manual-footer {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          color: var(--tg-mute, #8f9eb0);
        }
        .manual-count { font-variant-numeric: tabular-nums; }
      `}</style>
    </OnboardingShell>
  )
}
